import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  CallScoringData,
  CallScoringFilters,
} from "@/modules/transcripts/features/CallScoring/types";
import { UnifiedCall } from "@/modules/callIntelligence/types";

const CACHE_TTL_MS = 300000; // 5 minutes

export interface DetailCacheEntry {
  data: UnifiedCall;
  fetchedAt: number;
}

export interface CallScoringState {
  data: CallScoringData[];
  loading: boolean;
  showLoadingIndicator: boolean;
  error: string | null;
  totalCount: number;
  pagination: {
    pageIndex: number;
    pageSize: number;
  };
  filters: CallScoringFilters;
  sorting: {
    id: string;
    desc: boolean;
  }[];
  selectedCallId: string | null;
  detailCache: Record<string, DetailCacheEntry>;
}

const initialState: CallScoringState = {
  data: [],
  loading: false,
  showLoadingIndicator: false,
  error: null,
  totalCount: 0,
  pagination: {
    pageIndex: 0,
    pageSize: 25,
  },
  filters: {},
  sorting: [{ id: "createdAt", desc: true }], // Default: newest first
  selectedCallId: null,
  detailCache: {},
};

const callScoringSlice = createSlice({
  name: "callScoring",
  initialState,
  reducers: {
    setCallScoringData: (
      state,
      action: PayloadAction<{ data: CallScoringData[]; totalCount: number }>,
    ) => {
      state.data = action.payload.data;
      state.totalCount = action.payload.totalCount;
      state.error = null;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setShowLoadingIndicator: (state, action: PayloadAction<boolean>) => {
      state.showLoadingIndicator = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.loading = false;
      state.showLoadingIndicator = false;
    },
    setPagination: (
      state,
      action: PayloadAction<{ pageIndex: number; pageSize: number }>,
    ) => {
      state.pagination = action.payload;
    },
    setFilters: (state, action: PayloadAction<CallScoringFilters>) => {
      state.filters = action.payload;
    },
    updateFilter: (
      state,
      action: PayloadAction<{ key: string; value: any }>,
    ) => {
      state.filters = {
        ...state.filters,
        [action.payload.key]: action.payload.value,
      };
    },
    setSorting: (
      state,
      action: PayloadAction<{ id: string; desc: boolean }[]>,
    ) => {
      state.sorting = action.payload;
    },
    resetFilters: (state) => {
      state.filters = {};
      state.pagination.pageIndex = 0;
    },
    setSelectedCallId: (state, action: PayloadAction<string | null>) => {
      state.selectedCallId = action.payload;
    },
    cacheCallDetail: (
      state,
      action: PayloadAction<{ callId: string; data: UnifiedCall }>,
    ) => {
      state.detailCache[action.payload.callId] = {
        data: action.payload.data,
        fetchedAt: Date.now(),
      };
    },
    clearExpiredCache: (state) => {
      const now = Date.now();
      for (const key of Object.keys(state.detailCache)) {
        if (now - state.detailCache[key].fetchedAt > CACHE_TTL_MS) {
          delete state.detailCache[key];
        }
      }
    },
  },
});

export const {
  setCallScoringData,
  setLoading,
  setShowLoadingIndicator,
  setError,
  setPagination,
  setFilters,
  updateFilter,
  setSorting,
  resetFilters,
  setSelectedCallId,
  cacheCallDetail,
  clearExpiredCache,
} = callScoringSlice.actions;

export default callScoringSlice.reducer;
